import React, { useEffect, useRef, useState } from 'react';
import { products } from '../mockData';
import ProductGalleryModal from './ProductGalleryModal';

// Card art direction is 3:4. Trimming the top and bottom off a tall photo is
// safe — the subject sits in the middle. Trimming the sides off a landscape one
// throws away half the shot, so those get letterboxed instead.
const CARD_ASPECT = 3 / 4;
const MAX_WIDTH_LOSS = 0.25;

const fitImage = (img) => {
  const { naturalWidth: w, naturalHeight: h } = img;
  if (!w || !h) return;
  const widthLoss = w / h > CARD_ASPECT ? 1 - CARD_ASPECT / (w / h) : 0;
  img.parentElement.classList.toggle('is-letterboxed', widthLoss > MAX_WIDTH_LOSS);
};

const Products = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [cardTransforms, setCardTransforms] = useState({});
  const sectionRef = useRef(null);
  const cardsRef = useRef([]);
  const carouselRef = useRef(null);

  useEffect(() => {
    const currentSection = sectionRef.current;
    
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
        }
      },
      { threshold: 0.1 }
    );

    if (currentSection) {
      observer.observe(currentSection);
    }

    return () => {
      if (currentSection) {
        observer.unobserve(currentSection);
      }
    };
  }, []);

  useEffect(() => {
    if (!isVisible) return;

    const carousel = carouselRef.current;
    if (!carousel) return;

    let animationFrameId = null;
    let isScrolling = false;

    const updateCardTransforms = () => {
      const carouselRect = carousel.getBoundingClientRect();
      const carouselCenter = carouselRect.left + carouselRect.width / 2;

      cardsRef.current.forEach((card) => {
        if (!card) return;

        const cardRect = card.getBoundingClientRect();
        const cardCenter = cardRect.left + cardRect.width / 2;
        
        // Calculate distance from carousel center
        const distanceFromCenter = Math.abs(cardCenter - carouselCenter);
        const maxDistance = carouselRect.width / 2 + 200;
        
        // Normalize distance (0 = center, 1 = far)
        const normalizedDistance = Math.min(distanceFromCenter / maxDistance, 1);
        
        // Smooth easing function
        const eased = normalizedDistance * normalizedDistance;
        
        // Calculate transforms
        const translateY = eased * 30;
        const scale = 1 - (eased * 0.08);
        const opacity = 1 - (eased * 0.25);
        
        // Check if card is in viewport
        const isInView = cardRect.right > carouselRect.left - 100 && cardRect.left < carouselRect.right + 100;
        
        // Apply transforms directly to DOM for better performance
        if (isInView) {
          card.style.transform = `translateY(${translateY}px) scale(${scale})`;
          card.style.opacity = opacity;
        } else {
          card.style.transform = 'translateY(50px) scale(0.92)';
          card.style.opacity = '0.3';
        }
      });

      isScrolling = false;
    };

    const handleScroll = () => {
      if (!isScrolling) {
        isScrolling = true;
        if (animationFrameId) {
          cancelAnimationFrame(animationFrameId);
        }
        animationFrameId = requestAnimationFrame(updateCardTransforms);
      }
    };

    // Initial render
    updateCardTransforms();

    // Smooth scroll handling
    carousel.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', updateCardTransforms);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      carousel.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', updateCardTransforms);
    };
  }, [isVisible]);

  return (
    <section
      id="products"
      className="products-section"
      ref={sectionRef}
      aria-labelledby="products-title"
    >
      <div className="products-header">
        <span className="eyebrow" data-paper>The suite</span>
        <div className="products-header-row">
          <h2 className="products-title" id="products-title" data-paper data-paper-delay={90}>
            Everything the garment wears
          </h2>
          <p className="products-intro" data-paper data-paper-delay={180}>
            Ten custom packaging pieces for clothing brands, from the hang tag on the garment
            to the mailer it ships in. Tap any piece to see it up close.
          </p>
        </div>
      </div>

      <div
        className="products-carousel"
        ref={carouselRef}
        role="group"
        aria-label="Aureco packaging products, scroll horizontally"
      >
        <div className="products-track">
          {products.map((product, index) => {
            return (
              <div
                key={product.id}
                ref={(el) => (cardsRef.current[index] = el)}
                data-card-id={product.id}
                className="product-card"
                style={{
                  transition: 'transform 0.3s cubic-bezier(0.25, 0.46, 0.45, 0.94), opacity 0.3s ease'
                }}
                role="button"
                tabIndex={0}
                aria-label={`${product.name}: view details and request a quote`}
                onClick={() => setSelectedProduct(product)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setSelectedProduct(product);
                  }
                }}
              >
                <div className="product-media" style={{ '--fill': `url(${product.image})` }}>
                  <img
                    src={product.image}
                    alt={product.alt || product.name}
                    loading="lazy"
                    onLoad={(e) => fitImage(e.currentTarget)}
                  />
                  <div className="product-overlay" />
                </div>
                <div className="product-content">
                  <div className="product-caption-row">
                    <h3 className="product-name">{product.name}</h3>
                    <span className="product-view" aria-hidden="true">View</span>
                  </div>
                  <p className="product-description">{product.description}</p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {selectedProduct && (
        <ProductGalleryModal
          product={selectedProduct}
          onClose={() => setSelectedProduct(null)}
        />
      )}
    </section>
  );
};

export default Products;
